import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { DrawerCloseEvent, DrawerOpenEvent, DrawerComponent } from '@theme/drawer';
import { searchSuggestions } from './search-suggestions.js';
import { malachyteAPI } from './malachyte-api.js';

/**
 * A custom element that allows the user to search using the Malachyte API.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} searchResults - The search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [viewAllButton] - The view all results button.
 * @extends {Component<Refs>}
 */
class MalachyteSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchResults', 'resetButton'];

  #currentQuery = '';
  #currentPage = 1;
  #pageSize = 8;
  #suggestionsReady = false;
  #recommendedProducts = null;
  #drawerObserver = null;
  #controller = new AbortController();
  #isHandlingDrawerOpen = false;
  // @ts-ignore - MSStream is a legacy IE property
  #isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Default collections to show in no results state (fallback)
  #defaultCollections = [
    {
      handle: 'all-workwear',
      title: 'All Workwear',
      image: ''
    },
    {
      handle: 'boots',
      title: 'Boots',
      image: ''
    },
    {
      handle: 'clothing',
      title: 'Clothing',
      image: ''
    },
    {
      handle: 'accessories',
      title: 'Accessories',
      image: ''
    }
  ];

  // Collections to use (from admin or default)
  #featuredCollections = null;

  /**
   * Get the drawer component.
   * @returns {DrawerComponent | null} The drawer component.
   */
  get drawer() {
    return this.closest('drawer-component');
  }

  /**
   * Get featured collections from data attribute or use defaults
   * @returns {Array} Array of collection objects
   */
  #getFeaturedCollections() {
    // Return cached value if already loaded
    if (this.#featuredCollections !== null) {
      return this.#featuredCollections;
    }

    // Try to get collections from data attribute
    const collectionsData = this.dataset.collections;
    if (collectionsData) {
      try {
        this.#featuredCollections = JSON.parse(collectionsData);
        return this.#featuredCollections;
      } catch (error) {
        // Fall back to defaults on parse error
      }
    }

    // Fall back to default collections
    this.#featuredCollections = this.#defaultCollections;
    return this.#featuredCollections;
  }

  connectedCallback() {
    super.connectedCallback();

    const { drawer } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput?.value.length > 0) {
      this.#showResetButton();
    }

    // Try multiple approaches to listen for drawer open
    if (drawer) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      drawer.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose, { signal });
      drawer.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
      // Also listen on this component itself (like compare-drawer does)
      this.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
      this.addEventListener('click', this.#handleDrawerClick, { signal });
      
      // Set up MutationObserver as fallback to watch for drawer open attribute
      const drawerElement = drawer.refs?.drawer;
      
      if (drawerElement) {
        // Check if already open
        if (drawerElement.hasAttribute('open')) {
          setTimeout(() => {
            this.#handleDrawerOpen();
          }, 0);
        }
        
        // Watch for open attribute changes as fallback
        this.#drawerObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
              const isOpen = drawerElement.hasAttribute('open');
              if (isOpen) {
                this.#handleDrawerOpen();
              }
            }
          });
        });
        
        this.#drawerObserver.observe(drawerElement, {
          attributes: true,
          attributeFilter: ['open']
        });
      }
    } else {
      // Try to find it with a delay in case it's not ready yet
      setTimeout(() => {
        const foundDrawer = this.closest('drawer-component');
        if (foundDrawer) {
          foundDrawer.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
          foundDrawer.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose, { signal });
          
          // Also set up observer on found drawer
          const drawerElement = foundDrawer.refs?.drawer;
          if (drawerElement) {
            this.#drawerObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                  if (drawerElement.hasAttribute('open')) {
                    this.#handleDrawerOpen();
                  }
                }
              });
            });
            this.#drawerObserver.observe(drawerElement, {
              attributes: true,
              attributeFilter: ['open']
            });
          }
        }
      }, 100);
    }

    // Initialize search suggestions
    this.#initializeSuggestions();

    onDocumentLoaded(() => {
      this.resetSearch(false);
    });
  }

  /**
   * Initialize search suggestions
   */
  async #initializeSuggestions() {
    try {
      if (!searchSuggestions.isIndexLoaded()) {
        await searchSuggestions.fetchAndBuildIndex();
      }
      
      this.#suggestionsReady = true;
      
      // Don't fetch recommended products here - let #handleDrawerOpen do it
      // This prevents duplicate requests and cancellation issues
      
      // Show default suggestions and recommended products on initial load only if drawer is already open
      const drawer = this.drawer;
      const drawerElement = drawer?.refs?.drawer;
      if (drawerElement && drawerElement.hasAttribute('open') && this.refs.searchInput.value.trim().length === 0) {
        // Only show initial state if drawer is already open when component connects
        await this.#showInitialState();
      }
    } catch (error) {
      this.#suggestionsReady = false;
      // Fail gracefully - search will still work without suggestions
    }
  }

  /**
   * Handles clicks within the search drawer to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleDrawerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Prevent form submission - search stays in drawer
   * @param {Event} event - The submit event
   */
  handleFormSubmit = (event) => {
    event.preventDefault();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#drawerObserver) {
      this.#drawerObserver.disconnect();
      this.#drawerObserver = null;
    }
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.drawer?.toggleDrawer();
    }
  };

  /**
   * Handles the drawer open event.
   * Ensures the initial state (base query/recommended products) is shown when drawer opens.
   */
  #handleDrawerOpen = async () => {
    // Prevent concurrent executions
    if (this.#isHandlingDrawerOpen) {
      return;
    }
    
    this.#isHandlingDrawerOpen = true;
    
    try {
      // Reset search input and state
      this.#currentQuery = '';
      this.#currentPage = 1;
      this.#hideResetButton();
      
      if (this.refs.searchInput) {
        this.refs.searchInput.value = '';
      }

      // Show skeleton loaders immediately
      this.#showInitialSkeletonLoaders();

      // Always fetch recommended products when dialog opens (API handles caching)
      try {
        this.#recommendedProducts = await malachyteAPI.getRecommendedProducts({ k: 4 });
      } catch (error) {
        this.#recommendedProducts = null;
      }

      // Ensure suggestions are ready
      if (!this.#suggestionsReady) {
        try {
          if (!searchSuggestions.isIndexLoaded()) {
            await searchSuggestions.fetchAndBuildIndex();
          }
          this.#suggestionsReady = true;
        } catch (error) {
          // Fail silently
        }
      }

      // Show initial state with recommended products and suggestions
      try {
        await this.#showInitialState();
      } catch (error) {
        // Fallback to simple empty state
        if (this.refs.searchResults) {
          this.refs.searchResults.innerHTML = `
            <div class="search-results__inner">
              <div class="search-results__no-results">
                <p>${Theme.translations?.content?.search_input_placeholder || 'Start typing to search...'}</p>
              </div>
            </div>
          `;
        }
      }

      // Focus the input (skip autofocus on iOS to prevent autofill bar)
      if (this.refs.searchInput && !this.#isIOS) {
        this.refs.searchInput.focus();
      }
    } finally {
      // Always reset the flag, even if there's an error
      this.#isHandlingDrawerOpen = false;
    }
  };

  /**
   * Handles the drawer close event.
   */
  #handleDrawerClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll('.search-results__wrapper-products, .search-results__list')
    );

    const allItems = containers
      .flatMap((container) => {
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    let activeItemFound = false;
    
    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        activeItemFound = true;
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
          
          // Update aria-activedescendant on the combobox input
          let itemId = item.id;
          if (!itemId) {
            itemId = `malachyte-search-option-${itemIndex}`;
            item.id = itemId;
          }
          this.refs.searchInput.setAttribute('aria-activedescendant', itemId);
        } else {
          // Clear aria-activedescendant when keyboard navigation is inactive
          this.refs.searchInput.removeAttribute('aria-activedescendant');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    
    // Clear aria-activedescendant when no item is active (index is -1 or out of bounds)
    if (!activeItemFound) {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
    
    // Update status region to announce selection changes
    if (this.#isKeyboardNavigation) {
      this.#updateStatusRegion(this.#allResultsItems.length, activeItemFound ? index : -1);
    }
    
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Updates the status region to announce the number of available options and current selection.
   * @param {number} totalItems - Total number of available options
   * @param {number} currentIndex - Current selected index (-1 if none)
   */
  #updateStatusRegion(totalItems, currentIndex) {
    const statusElement = document.getElementById('malachyte-search-status');
    if (!statusElement) return;

    if (totalItems === 0) {
      statusElement.textContent = '';
      return;
    }

    if (currentIndex >= 0 && currentIndex < totalItems) {
      statusElement.textContent = `${currentIndex + 1} of ${totalItems} options`;
    } else {
      statusElement.textContent = `${totalItems} option${totalItems === 1 ? '' : 's'} available`;
    }
  }

  /**
   * Navigate through the search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        event.preventDefault();
        if (this.#currentIndex >= 0) {
          // If a result is selected, click it
          this.#currentItem?.querySelector('a')?.click();
        } else if (this.refs.searchInput.value.trim().length > 0) {
          // If no result is selected but there's a query, trigger a search
          this.search();
        }
        // If no query, do nothing
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Strip HTML tags and special characters from input
   * @param {string} str - Input string
   * @returns {string} Sanitized string with HTML removed
   */
  #stripHtmlSpecialChars(str) {
    // Remove HTML tags
    const withoutTags = str.replace(/<[^>]*>/g, '');
    
    // Remove potentially dangerous characters
    return withoutTags
      .replace(/[<>]/g, '')
      .replace(/&lt;/g, '')
      .replace(/&gt;/g, '')
      .replace(/&#?\w+;/g, ''); // Remove HTML entities
  }

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   */
  search = debounce(async (event) => {
    const rawValue = this.refs.searchInput.value;
    const searchTerm = this.#stripHtmlSpecialChars(rawValue).trim();
    
    // Update input field with sanitized value if it changed
    if (rawValue !== searchTerm && rawValue.trim() !== searchTerm) {
      this.refs.searchInput.value = searchTerm;
    }
    
    this.#currentIndex = -1;
    this.#currentQuery = searchTerm;
    this.#currentPage = 1;

    if (!searchTerm.length) {
      this.#hideResetButton();
      // Show initial state with recommendations and suggestions
      if (this.#suggestionsReady) {
        try {
          await this.#showInitialState();
        } catch (error) {
          this.#resetSearch();
        }
      } else {
        this.#resetSearch();
      }
      return;
    }

    this.#showResetButton();

    // Track the search if suggestions are ready
    if (this.#suggestionsReady) {
      try {
        searchSuggestions.trackSearch(searchTerm);
      } catch (error) {
        // Fail silently
      }
    }

    // Track search in Monetate
    if (window.monetateQ) {
      window.monetateQ.push([
        'addSearch',
        {
          searchTerm: searchTerm,
          searchType: 'site'
        }
      ]);
      window.monetateQ.push(['trackData']);
    }
    
    // Perform search for all queries (minimum 1 character)
    this.#getSearchResults(searchTerm);
  }, 300);

  /**
   * Render suggestions section using reusable template
   * @param {string[]} suggestions - Array of suggestion strings
   * @param {string} searchTerm - The current search term to highlight
   * @returns {DocumentFragment|null} Document fragment with suggestions section or null if empty
   */
  #renderSuggestionsSection(suggestions, searchTerm = '') {
    // Prepare suggestions list with user's query first (if there's a search term)
    let allSuggestions = [...suggestions];
    
    if (searchTerm && searchTerm.trim()) {
      // Add user's typed query as first suggestion if it's not already in the list
      const trimmedTerm = searchTerm.trim();
      if (!allSuggestions.some(s => s.toLowerCase() === trimmedTerm.toLowerCase())) {
        allSuggestions = [trimmedTerm, ...allSuggestions];
      }
    }
    
    if (!allSuggestions || allSuggestions.length === 0) {
      return null;
    }

    const template = document.getElementById('malachyte-search-suggestions-section-template');
    if (!template) {
      return null;
    }

    const section = template.content.cloneNode(true);
    const suggestionsList = section.querySelector('[data-suggestions-list]');
    const suggestionTemplate = document.getElementById('malachyte-search-suggestion-template');

    if (suggestionTemplate) {
      allSuggestions.forEach((suggestion, index) => {
        const item = suggestionTemplate.content.cloneNode(true);
        const li = item.querySelector('li');
        const button = item.querySelector('button');
        const span = item.querySelector('span');

        li.setAttribute('data-search-result-index', `search-results-${index}`);
        button.setAttribute('data-suggestion', suggestion);
        // Note: span is text-only; button's data-suggestion provides accessible name
        
        // Bold the matching part of the search term using safe DOM manipulation
        if (searchTerm && searchTerm.trim()) {
          // Escape regex special characters in search term
          const escapedTerm = searchTerm.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(${escapedTerm})`, 'gi');
          
          // Sanitize suggestion to prevent XSS
          const sanitizedSuggestion = this.#stripHtmlSpecialChars(suggestion);
          
          // Clear span and build content programmatically
          span.textContent = '';
          
          // Find all matches and their positions using matchAll
          const matches = Array.from(sanitizedSuggestion.matchAll(regex)).map(match => ({
            index: match.index,
            length: match[0].length,
            text: match[0]
          }));
          
          // Build DOM structure: text nodes and <strong> elements
          let lastIndex = 0;
          for (const match of matches) {
            // Add text before match
            if (match.index > lastIndex) {
              const textNode = document.createTextNode(sanitizedSuggestion.substring(lastIndex, match.index));
              span.appendChild(textNode);
            }
            
            // Add <strong> element with matched text
            const strong = document.createElement('strong');
            const strongText = document.createTextNode(match.text);
            strong.appendChild(strongText);
            span.appendChild(strong);
            
            lastIndex = match.index + match.length;
          }
          
          // Add remaining text after last match
          if (lastIndex < sanitizedSuggestion.length) {
            const textNode = document.createTextNode(sanitizedSuggestion.substring(lastIndex));
            span.appendChild(textNode);
          }
        } else {
          // No search term, just set plain text
          span.textContent = suggestion;
        }

        suggestionsList.appendChild(item);
      });
    }

    return section;
  }

  /**
   * Render collection cards
   * @param {Array} collections - Array of collection objects
   * @returns {DocumentFragment} Document fragment with collection cards
   */
  #renderCollectionCards(collections) {
    const template = document.getElementById('malachyte-search-collection-card-template');
    if (!template) {
      return document.createDocumentFragment();
    }

    const fragment = document.createDocumentFragment();
    
    collections.forEach((collection) => {
      const card = template.content.cloneNode(true);
      const link = card.querySelector('.resource-card__link');
      const hiddenSpan = card.querySelector('.visually-hidden');
      const titleEl = card.querySelector('.resource-card__title');
      const image = card.querySelector('.resource-card__image');

      const collectionUrl = `/collections/${collection.handle}`;
      
      // Set attributes
      link.setAttribute('href', collectionUrl);
      hiddenSpan.textContent = collection.title;
      titleEl.textContent = collection.title;
      
      // Set image
      if (collection.image) {
        image.setAttribute('src', collection.image);
        image.setAttribute('alt', collection.title);
      } else {
        // Create placeholder if no image
        const placeholder = document.createElement('div');
        placeholder.className = 'resource-card__image-placeholder';
        placeholder.textContent = collection.title;
        image.parentElement.replaceChild(placeholder, image);
      }

      fragment.appendChild(card);
    });

    return fragment;
  }

  /**
   * Render collections section using dedicated template
   * @param {Array} collections - Array of collection objects
   * @param {string} title - Section title (e.g., "Shop by Category")
   * @returns {DocumentFragment|null} Document fragment with collections section or null if empty
   */
  #renderCollectionsSection(collections, title = 'Collections') {
    if (!collections || collections.length === 0) {
      return null;
    }

    const template = document.getElementById('malachyte-search-collections-section-template');
    if (!template) {
      return null;
    }

    const section = template.content.cloneNode(true);
    const titleEl = section.querySelector('[data-title]');
    const collectionsList = section.querySelector('[data-collections-list]');

    if (titleEl) {
      titleEl.textContent = title;
    }

    const collectionCards = this.#renderCollectionCards(collections);
    collectionsList.appendChild(collectionCards);

    return section;
  }

  /**
   * Render products section using reusable template
   * @param {Array} products - Array of product objects
   * @param {string} title - Section title (e.g., "Recommended", "Results")
   * @returns {DocumentFragment|null} Document fragment with products section or null if empty
   */
  #renderProductsSection(products, title = 'Products') {
    if (!products || products.length === 0) {
      return null;
    }

    const template = document.getElementById('malachyte-search-products-section-template');
    if (!template) {
      return null;
    }

    const section = template.content.cloneNode(true);
    const titleEl = section.querySelector('[data-title]');
    const productsList = section.querySelector('[data-products-list]');

    if (titleEl) {
      titleEl.textContent = title;
    }

    const productCards = this.#renderProductCards(products);
    productsList.appendChild(productCards);

    return section;
  }

  /**
   * Show skeleton loaders for initial state
   */
  #showInitialSkeletonLoaders() {
    this.#showSkeletonLoaders(4, 'Best Sellers');
  }

  /**
   * Show initial state with recommended products and suggestions
   */
  async #showInitialState() {
    const { searchResults } = this.refs;
    
    // Set initial state max-height class
    this.classList.remove('lg:max-h-[578px]');
    this.classList.add('lg:max-h-[501px]');

    // Check if suggestions are ready
    if (!this.#suggestionsReady) {
      searchResults.innerHTML = `
        <div class="search-results__inner">
          <div class="search-results__no-results">
            <p>${Theme.translations?.content?.search_input_placeholder || 'Start typing to search...'}</p>
          </div>
        </div>
      `;
      return;
    }

    const template = document.getElementById('malachyte-search-initial-state-template');
    if (!template) {
      return;
    }

    const content = template.content.cloneNode(true);
    const suggestionsContainer = content.querySelector('[data-section-container="suggestions"]');
    const productsContainer = content.querySelector('[data-section-container="products"]');

    // Render suggestions (no search term for initial state, so no highlighting)
    const suggestions = await searchSuggestions.getSuggestions('', 5);
    const suggestionsSection = this.#renderSuggestionsSection(suggestions, '');
    if (suggestionsSection) {
      suggestionsContainer.appendChild(suggestionsSection);
    } else {
      suggestionsContainer.remove();
    }

    // Render recommended products
    const productsSection = this.#renderProductsSection(
      this.#recommendedProducts, "Best Sellers"
    );
    if (productsSection) {
      productsContainer.appendChild(productsSection);
    } else {
      productsContainer.remove();
    }

    searchResults.innerHTML = '';
    searchResults.appendChild(content);
    this.#resetScrollPositions();

    // Update status region after results are rendered
    setTimeout(() => {
      const totalItems = this.#allResultsItems?.length || 0;
      this.#updateStatusRegion(totalItems, -1);
    }, 0);
  }


  /**
   * Handle suggestion selection
   * @param {MouseEvent} event - The click event
   */
  selectSuggestion = (event) => {
    const button = event.target.closest('button');
    if (!button) return;

    const rawSuggestion = button.getAttribute('data-suggestion');
    if (!rawSuggestion) return;

    // Sanitize the suggestion before using it
    const suggestion = this.#stripHtmlSpecialChars(rawSuggestion).trim();
    if (!suggestion) return;

    // Set the input value and trigger search
    this.refs.searchInput.value = suggestion;
    this.#currentQuery = suggestion;
    this.#showResetButton();

    // Perform the search
    this.#getSearchResults(suggestion);
  };

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.searchResults.querySelector('.search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Show skeleton loaders while loading
   * @param {number} [cardCount] - Number of skeleton cards to show (defaults to pageSize)
   * @param {string} [title='Results'] - Title for the products section
   */
  #showSkeletonLoaders(cardCount, title = 'Results') {
    const { searchResults } = this.refs;
    const template = document.getElementById('malachyte-search-loading-template');

    if (!template) {
      return;
    }

    const content = template.content.cloneNode(true);
    const skeletonContainer = content.querySelector('[data-skeleton-container]');
    const titleElement = content.querySelector('[data-loading-title]');

    // Set the title
    if (titleElement) {
      titleElement.textContent = title;
    }

    if (skeletonContainer) {
      // Add skeleton cards (use provided count or default to pageSize)
      const count = cardCount !== undefined ? cardCount : this.#pageSize;
      const skeletonTemplate = document.getElementById('malachyte-search-skeleton-card-template');
      if (skeletonTemplate) {
        for (let i = 0; i < count; i++) {
          const skeleton = skeletonTemplate.content.cloneNode(true);
          skeletonContainer.appendChild(skeleton);
        }
      }
    }

    searchResults.innerHTML = '';
    searchResults.appendChild(content);
  }

  /**
   * Fetch search results from Malachyte API and render them
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    // Show skeleton loaders immediately
    this.#showSkeletonLoaders();

    try {
      const { results: products } = await malachyteAPI.search(searchTerm, {
        k: this.#pageSize,
        logResponse: true,
        cancelPrevious: true,
      });

      if (products.length === 0) {
        this.#renderNoResults();
        return;
      }

      // Render products directly from API data
      await this.#renderProductsFromAPI(products, searchTerm);
    } catch (error) {
      if (error.message === 'Request cancelled') return;
      this.#renderNoResults();
    }
  }

  /**
   * Extract product handle from full URL
   * @param {string} handleUrl - Full URL like "https://project-maverick.myshopify.com/products/brunt-box"
   * @returns {string} Product handle
   */
  #extractHandle(handleUrl) {
    if (!handleUrl) return '';
    const match = handleUrl.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : '';
  }

  /**
   * Format price from dollars to display format
   * @param {number} priceInDollars - Price in dollars (e.g., 35.0, 20.99)
   * @returns {{floor: string, sup: string}} Formatted price
   */
  #formatPrice(priceInDollars) {
    if (!priceInDollars && priceInDollars !== 0) return { floor: '', sup: '' };

    const currencySymbol = Theme.translations?.currency_symbol || '$';
    const dollars = Math.floor(priceInDollars);
    const cents = Math.round((priceInDollars - dollars) * 100);

    return {
      floor: `${currencySymbol}${dollars}`,
      sup: cents > 0 ? String(cents).padStart(2, '0') : '',
    };
  }

  /**
   * Calculate price range from variants
   * @param {Array} variants - Array of variant objects
   * @returns {{min: number, max: number, varies: boolean}} Price range info
   */
  #calculatePriceRange(variants) {
    if (!variants || variants.length === 0) {
      return { min: 0, max: 0, varies: false };
    }

    const prices = variants
      .map((v) => parseFloat(v.price) || 0)
      .filter((p) => p > 0);

    if (prices.length === 0) {
      return { min: 0, max: 0, varies: false };
    }

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const varies = min !== max;

    return { min, max, varies };
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Strip HTML tags from a string safely without DOM XSS risk
   * @param {string} html - HTML string to strip
   * @returns {string} Plain text
   */
  #stripHtmlTags(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.textContent || '';
  }

  /**
   * Render product cards from product data using template
   * @param {Array} products - Array of product objects from API
   * @returns {DocumentFragment} Document fragment with product cards
   */
  #renderProductCards(products) {
    const template = document.getElementById('malachyte-search-product-card-template');
    if (!template) {
      return document.createDocumentFragment();
    }

    const fragment = document.createDocumentFragment();

    products.forEach((result) => {
      const product = result.facts;
      if (!product) return;

      const handle = this.#extractHandle(product.handle);
      const productUrl = handle ? `/products/${handle}` : '#';
      const productId = product.id;
      const title = product.title || '';

      // Extract description from global.title_tag metafield
      // Format: "Product Name | Description | Brand"
      // We want the middle part (Description)
      let description = '';
      const titleTag = product?.metafields["global.title_tag"] || '';

      if (titleTag) {
        const parts = titleTag.split('|');
        if (parts.length > 2) {
          description = parts[1].trim();
        }
      }

      const imageUrl = product.images && product.images.length > 0 ? product.images[0] : '';
      const secondaryImageUrl = product.images && product.images.length > 1 ? product.images[1] : '';

      // Calculate price range from variants
      const priceRange = this.#calculatePriceRange(product.variants);
      const priceMin = this.#formatPrice(priceRange.min);
      const priceMax = this.#formatPrice(priceRange.max);

      // Clone template
      const card = template.content.cloneNode(true);
      const wrapper = card.querySelector('.search-results__card');
      const link = card.querySelector('.resource-card__link');
      const hiddenSpan = card.querySelector('.visually-hidden');
      const titleEl = card.querySelector('.resource-card__title');
      const descriptionEl = card.querySelector('.resource-card__description');
      const priceEl = card.querySelector('.priceRange');
      const priceSpan = card.querySelector('.igPrice');
      const priceMinSpan = card.querySelector('.igPriceMin');
      const priceMaxSpan = card.querySelector('.igPriceMax');
      const priceSeparator = card.querySelector('.price-separator');
      const image = card.querySelector('.resource-card__image');
      const secondaryImage = card.querySelector('.resource-card__image--secondary');

      // Set attributes
      wrapper.setAttribute('data-product-id', productId);
      link.setAttribute('href', productUrl);
      hiddenSpan.textContent = title;
      titleEl.textContent = title;

      // Set description
      if (description) {
        const plainDescription = this.#stripHtmlTags(description);
        descriptionEl.textContent = plainDescription;
        descriptionEl.style.display = '';
      }

      // Set images
      if (imageUrl) {
        image.setAttribute('src', imageUrl);
        image.setAttribute('alt', title);
        if (secondaryImageUrl) {
          secondaryImage.setAttribute('src', secondaryImageUrl);
          secondaryImage.setAttribute('alt', title);
          secondaryImage.style.display = '';
        }
      } else {
        // Create placeholder
        const placeholder = document.createElement('div');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.className = 'resource-card__image-placeholder';
        placeholder.textContent = title.substring(0, 60);
        image.parentElement.replaceChild(placeholder, image);
      }

      // Set price
      if (priceRange.min > 0) {
        priceEl.style.display = '';
        if (priceRange.varies) {
          priceSpan.style.display = 'none';
          priceMinSpan.style.display = '';
          priceSeparator.style.display = '';
          priceMaxSpan.style.display = '';
          priceMinSpan.setAttribute('data-product-id', productId);
          priceMaxSpan.setAttribute('data-product-id', productId);
          priceMinSpan.innerHTML = `${priceMin.floor}${priceMin.sup ? `<sup class="top-[-0.2em]">${priceMin.sup}</sup>` : ''}`;
          priceMaxSpan.innerHTML = `${priceMax.floor}${priceMax.sup ? `<sup class="top-[-0.2em]">${priceMax.sup}</sup>` : ''}`;
        } else {
          priceSpan.style.display = '';
          priceMinSpan.style.display = 'none';
          priceSeparator.style.display = 'none';
          priceMaxSpan.style.display = 'none';
          priceSpan.setAttribute('data-product-id', productId);
          priceSpan.innerHTML = `${priceMin.floor}${priceMin.sup ? `<sup class="top-[-0.2em]">${priceMin.sup}</sup>` : ''}`;
        }
      }

      fragment.appendChild(card);
    });

    return fragment;
  }

  /**
   * Render products directly from Malachyte API response
   * @param {Array} products - Array of product objects from API
   * @param {string} searchTerm - The search term
   */
  async #renderProductsFromAPI(products, searchTerm) {
    const { searchResults } = this.refs;

    // Set search results max-height class
    this.classList.remove('lg:max-h-[501px]');
    this.classList.add('lg:max-h-[578px]');

    if (products.length === 0) {
      this.#renderNoResults();
      return;
    }

    const template = document.getElementById('malachyte-search-results-template');
    if (!template) {
      return;
    }

    const content = template.content.cloneNode(true);
    const suggestionsContainer = content.querySelector('[data-section-container="suggestions"]');
    const productsContainer = content.querySelector('[data-section-container="products"]');

    // Render suggestions with highlighting
    if (this.#suggestionsReady) {
      const suggestions = await searchSuggestions.getSuggestions(searchTerm, 5);
      const suggestionsSection = this.#renderSuggestionsSection(suggestions, searchTerm);
      if (suggestionsSection) {
        suggestionsContainer.appendChild(suggestionsSection);
      } else {
        suggestionsContainer.remove();
      }
    } else {
      suggestionsContainer.remove();
    }

    // Render products
    const productsSection = this.#renderProductsSection(
      products,
      Theme.translations?.content?.search_results_resource_products || 'Results'
    );
    if (productsSection) {
      // Find the shop-all container before appending (fragments become empty after append)
      const shopAllContainer = productsSection.querySelector('[data-shop-all-container]');

      // Add shop all button for search results only
      if (shopAllContainer) {
        const shopAllTemplate = document.getElementById('malachyte-search-shop-all-template');
        if (shopAllTemplate) {
          const shopAllContent = shopAllTemplate.content.cloneNode(true);
          shopAllContainer.appendChild(shopAllContent);
        }
      }

      // Now append the products section with the button included
      productsContainer.appendChild(productsSection);
    } else {
      productsContainer.remove();
    }

    searchResults.innerHTML = '';
    searchResults.appendChild(content);
    this.#resetScrollPositions();

    // Update status region after results are rendered
    setTimeout(() => {
      const totalItems = this.#allResultsItems?.length || 0;
      this.#updateStatusRegion(totalItems, -1);
    }, 0);
  }

  /**
   * Render no results message with featured collections
   */
  #renderNoResults() {
    const { searchResults } = this.refs;
    const searchTerm = this.#escapeHtml(this.#currentQuery);

    // Set no results max-height class
    this.classList.remove('lg:max-h-[501px]');
    this.classList.add('lg:max-h-[578px]');

    const template = document.getElementById('malachyte-search-no-results-template');
    if (!template) {
      return;
    }

    const content = template.content.cloneNode(true);
    const noResultsContainer = content.querySelector('[data-section-container="no-results-message"]');
    const collectionsContainer = content.querySelector('[data-section-container="collections"]');

    // Render no results message
    const noResultsTemplate = document.getElementById('malachyte-search-no-results-message-template');
    if (noResultsTemplate) {
      const noResultsSection = noResultsTemplate.content.cloneNode(true);
      const searchTermEl = noResultsSection.querySelector('[data-search-term]');
      if (searchTermEl) {
        searchTermEl.innerHTML = `No products were found for your search: ${searchTerm}`;
      }
      noResultsContainer.appendChild(noResultsSection);
    }

    // Render featured collections (from admin or defaults)
    const featuredCollections = this.#getFeaturedCollections();
    const collectionsSection = this.#renderCollectionsSection(
      featuredCollections,
      'Shop by Category'
    );
    if (collectionsSection) {
      collectionsContainer.appendChild(collectionsSection);
    } else {
      collectionsContainer.remove();
    }

    searchResults.innerHTML = '';
    searchResults.appendChild(content);
    this.#resetScrollPositions();

    // Update status region after results are rendered
    setTimeout(() => {
      const totalItems = this.#allResultsItems?.length || 0;
      this.#updateStatusRegion(totalItems, -1);
    }, 0);
  }

  /**
   * View all products in the all-workwear collection
   */
  viewAllResults = () => {
    window.location.href = '/collections/all-workwear';
  };

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = false;
  }

  #resetSearch = async () => {
    const { searchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#currentQuery = '';
    this.#currentPage = 1;
    this.#hideResetButton();

    // Reset to initial state max-height class
    this.classList.remove('lg:max-h-[578px]');
    this.classList.add('lg:max-h-[501px]');

    // Try to show initial state, fall back to empty state
    try {
      await this.#showInitialState();
    } catch (error) {
      // Fallback to simple empty state
      searchResults.innerHTML = `
        <div class="search-results__inner">
          <div class="search-results__no-results">
            <p>${Theme.translations?.content?.search_input_placeholder || 'Start typing to search...'}</p>
          </div>
        </div>
      `;
    }

    this.#resetScrollPositions();
  };
}

if (!customElements.get('malachyte-search-component')) {
  customElements.define('malachyte-search-component', MalachyteSearchComponent);
}

